/**
 * @file index.tsx
 *
 * @fileoverview Renders the job form duration input.
 */

import { useEffect, useContext } from 'react';
import { useIntl } from 'react-intl';
import addDays from 'date-fns/addDays';
import startOfDay from 'date-fns/startOfDay';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import { getCurrentFrontendConfig } from '~/jobiqo/context/frontendConfig';
import { AppContext } from '~/jobiqo/context/app';
import { AppContextInterface } from '~/jobiqo/models/app';
import { FIELD_JOB_PUBLISHING_DATE } from '~/jobiqo/components/Job/Form/components/PublishingDateInput';
import { Controller, useFormContext } from 'react-hook-form';
import { formatJobDate } from '~/jobiqo/services/jobs';
import { Input } from '~/vcl/atoms/01-forms/03-input';
import { FormDescription } from '~/vcl/atoms/01-forms/11-form-item/02-form-description';
import { InlineError } from '~/vcl/atoms/01-forms/09-inline-error';
import ExtendedRuntimeInfo from '~/jobiqo/components/Job/Form/components/ExtendedRuntimeInfo';
import { SingleDatePopup } from '~/vcl/atoms/01-forms/06-date/single';
import { deriveDateFormat, parseUniversalDate } from '~/jobiqo/utils/dates';

export const FIELD_JOB_DURATION = 'field_job_duration';
export const FIELD_JOB_EXPIRATION_DATE = 'field_job_expiration_date';

const durationDays = (end: Date, start: Date) =>
  Math.floor((end.getTime() - start.getTime()) / 86400000);
const addDurationDays = (start: Date, days: number) =>
  new Date(start.getTime() + days * 86400000);

interface JobFormDurationInputProps {
  /**
   * The maximum job value (extend runtime plus the default duration).
   */
  maxValue?: number;
  /**
   * The default value.
   */
  defaultValue?: number | null;
  /**
   * Extended runtime product is added.
   *
   * @default false
   */
  extendedRuntime?: boolean;
  /**
   * Use date picker instead of number input.
   *
   * @default false
   */
  useDatePicker?: boolean;
  /**
   * The initial publishing date used as fallback before the form state is ready.
   */
  initialPublishingDate?: string | null;
  /**
   * The saved expiration date when editing an already published job.
   */
  publishedJob?: { expiration: string; duration?: number };
}

export interface JobFormDurationFormField {
  /**
   * The job duration form field.
   */
  [FIELD_JOB_DURATION]: number;
}

export function JobFormDurationInput({
  maxValue,
  defaultValue,
  extendedRuntime = false,
  useDatePicker = false,
  initialPublishingDate,
  publishedJob,
}: JobFormDurationInputProps) {
  const intl = useIntl();
  const { locale }: AppContextInterface = useContext(AppContext);
  const dateFormat: string = deriveDateFormat(intl);
  const {
    setValue,
    resetField,
    control,
    formState: { errors, dirtyFields },
    watch,
  } = useFormContext();
  const watchPublishingDate = watch(FIELD_JOB_PUBLISHING_DATE);
  const watchDuration = watch(FIELD_JOB_DURATION);

  const publishingDate = publishedJob
    ? initialPublishingDate
    : watchPublishingDate || initialPublishingDate;
  // Published jobs derive their total duration from the saved dates. New and
  // unpublished jobs use the duration supplied by the parent form.
  const initialDuration = publishedJob
    ? publishedJob.expiration && publishingDate
      ? durationDays(
          parseUniversalDate(publishedJob.expiration),
          parseUniversalDate(publishingDate),
        )
      : undefined
    : defaultValue;
  // Use the backend/default duration until the user edits the field. This
  // prevents a stale form value from replacing the calculated initial value.
  const initialFormDuration = publishedJob?.duration ?? initialDuration;
  const effectiveDuration = dirtyFields[FIELD_JOB_DURATION]
    ? watchDuration ?? initialFormDuration
    : initialFormDuration;
  // Date-only scheduling uses the current time, matching the backend.
  const publicationTime = publishingDate
    ? parseUniversalDate(publishingDate)
    : new Date();
  if (
    typeof publishingDate === 'string' &&
    /^\d{4}-\d{2}-\d{2}$/.test(publishingDate)
  ) {
    const now = new Date();
    publicationTime.setHours(
      now.getHours(),
      now.getMinutes(),
      now.getSeconds(),
      0,
    );
  }
  const expirationDate =
    effectiveDuration && publishingDate
      ? addDurationDays(publicationTime, Number(effectiveDuration))
      : null;
  // Duration starts at publication, so published jobs must run at least until
  // today. Always require at least one day.
  const minDuration =
    publishedJob && publishingDate
      ? Math.max(
          1,
          Math.floor((Date.now() - publicationTime.getTime()) / 86400000) + 1,
        )
      : 1;

  const fieldsToHideOnEdit =
    getCurrentFrontendConfig().JOBS.JOB_EDIT_HIDDEN_FIELDS || [];
  const isReadOnly =
    !!publishedJob && fieldsToHideOnEdit.includes(FIELD_JOB_EXPIRATION_DATE);
  useEffect(() => {
    if (!isReadOnly) {
      resetField(FIELD_JOB_DURATION, {
        defaultValue: initialFormDuration,
      });
    }
  }, [maxValue, initialFormDuration, isReadOnly, resetField]);

  // Sites can disable expiration editing for published jobs. In that case,
  // display the saved expiration date instead of an editable duration.
  if (isReadOnly) {
    return (
      <>
        <Controller
          name={FIELD_JOB_DURATION}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              error={!!error}
              id={FIELD_JOB_DURATION}
              label={intl.formatMessage({
                id: 'job_expiration_date_label',
                defaultMessage: 'Expiration date',
              })}
              readOnly={true}
              value={formatJobDate(publishedJob.expiration, intl)}
            />
          )}
        />
        <FormDescription>
          {intl.formatMessage({
            id: 'job_expiration_date_description',
            defaultMessage:
              'You cannot change the expiration date for an already published job.',
          })}
        </FormDescription>
      </>
    );
  }

  // When an extended runtime product is selected, show its runtime information
  // instead of letting the user choose a duration manually.
  if (extendedRuntime) {
    return (
      <>
        <Controller
          name={FIELD_JOB_DURATION}
          control={control}
          defaultValue={effectiveDuration}
          render={({ field }) => <input {...field} type="hidden" />}
        />
        <ExtendedRuntimeInfo
          maxValue={maxValue}
          publishingDate={watchPublishingDate}
        />
      </>
    );
  }

  // Both input modes submit duration in days. Convert a selected end date to
  // the total duration from publication, including for already published jobs.
  const handleDateChange = (date: Date | null) => {
    if (date && publishingDate) {
      const duration = differenceInCalendarDays(
        date,
        parseUniversalDate(publishingDate),
      );
      setValue(FIELD_JOB_DURATION, duration > 0 ? duration : 1, {
        shouldDirty: true,
      });
    }
  };

  return (
    <>
      <Controller
        name={FIELD_JOB_DURATION}
        control={control}
        render={({ field, fieldState: { error } }) => {
          return useDatePicker ? (
            <SingleDatePopup
              id={FIELD_JOB_DURATION}
              label={intl.formatMessage({
                id: 'job_end_date_label',
                defaultMessage: 'End date',
              })}
              description={intl.formatMessage({
                id: 'job_end_date_description',
                defaultMessage:
                  'Select the date when the job should go offline',
              })}
              placeholder={intl.formatMessage({
                id: 'job_end_date_placeholder',
                defaultMessage: 'End date',
              })}
              error={!!error}
              initialDate={
                expirationDate ||
                (publishedJob
                  ? null
                  : addDays(
                      publishingDate
                        ? parseUniversalDate(publishingDate)
                        : new Date(),
                      defaultValue || 30,
                    ))
              }
              locale={locale}
              readOnly={extendedRuntime}
              displayFormat={dateFormat}
              minDate={
                publishedJob
                  ? startOfDay(new Date())
                  : publishingDate
                  ? addDays(parseUniversalDate(publishingDate), 1)
                  : new Date()
              }
              maxDate={
                publishingDate && (!publishedJob || maxValue)
                  ? addDays(parseUniversalDate(publishingDate), maxValue || 30)
                  : undefined
              }
              onDateChange={handleDateChange}
            />
          ) : (
            <Input
              {...field}
              value={effectiveDuration ?? ''}
              id={FIELD_JOB_DURATION}
              type="number"
              readOnly={extendedRuntime}
              error={!!error}
              label={intl.formatMessage({
                id: 'job_duration_label',
                defaultMessage: 'Days online',
              })}
              size={40}
              description={
                publishedJob
                  ? intl.formatMessage({
                      id: 'job_expiration_duration_description',
                      defaultMessage:
                        'Set the total number of days online, counted from the publishing date.',
                    })
                  : ((intl.formatMessage(
                      {
                        id: 'job_duration_description',
                        defaultMessage:
                          'The days online are set to the default {defaultValue} days.{br}If you need to take the job posting offline earlier, please adapt the day.',
                      },
                      {
                        defaultValue,
                        br: <br />,
                      },
                    ) as unknown) as string)
              }
              placeholder={intl.formatMessage({
                id: 'job_duration_placeholder',
                defaultMessage: 'Duration',
              })}
              min={minDuration}
              max={publishedJob ? maxValue || undefined : maxValue}
              onChange={(e) =>
                setValue(
                  FIELD_JOB_DURATION,
                  Number.parseInt(e.currentTarget.value),
                  { shouldDirty: true },
                )
              }
            />
          );
        }}
      />
      {expirationDate && !useDatePicker && (
        <FormDescription>
          {intl.formatMessage(
            {
              id: 'job_duration_expiration_datetime',
              defaultMessage:
                'Based on the start date and runtime, the job will go offline on {date} at {time}.',
            },
            {
              date: <b>{formatJobDate(expirationDate, intl)}</b>,
              time: <b>{intl.formatTime(expirationDate)}</b>,
            },
          )}
        </FormDescription>
      )}
      {errors[FIELD_JOB_DURATION] && (
        <InlineError message={errors[FIELD_JOB_DURATION]?.message as string} />
      )}
    </>
  );
}

export default JobFormDurationInput;
